import React from 'react'
import { Link } from 'react-router-dom'
import { Container } from './ui/Container'
import { toast } from 'sonner'
import {
  Heart,
  Mail,
  Phone,
  MapPin,
  Twitter,
  Facebook,
  Instagram,
  Linkedin,
  Shield,
  CheckCircle,
  Stethoscope,
  Award,
  Users,
  Calendar,
  MessageCircle,
  ArrowRight
} from 'lucide-react'
import { motion } from 'framer-motion'

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear()

  const handleComingSoon = (feature: string) => {
    toast.info(`🚀 ${feature} - Feature Coming Soon!`, {
      duration: 3000,
      position: 'top-center',
    });
  };

  interface FooterLink {
    name: string
    href: string
    icon?: React.ComponentType<{ className?: string }>
  }

  const footerSections: Record<string, FooterLink[]> = {
    solutions: [
      { name: 'Biomarker Testing', href: '/features', icon: Stethoscope },
      { name: 'AI Health Insights', href: '/features#ai', icon: Award },
      { name: 'Meal Planning', href: '/meal-planning', icon: Calendar },
      { name: 'Mealmate Integration', href: '/mealmate', icon: Users }
    ],
    company: [
      { name: 'About BioTune', href: '/about' },
      { name: 'Contact Us', href: '/contact' }
    ],
    resources: [
      { name: 'How BIOTUNE Works', href: '/how-it-works' },
      { name: 'Pricing', href: '/pricing' }
    ],
    legal: [
      { name: 'Privacy Policy', href: '/privacy' },
      { name: 'Terms of Service', href: '/terms' },
      { name: 'HIPAA Notice', href: '/hipaa' },
      { name: 'Accessibility', href: '/accessibility' },
      { name: 'Medical Disclaimer', href: '/disclaimer' }
    ]
  }

  const trustBadges = [
    {
      icon: Shield,
      text: 'HIPAA Compliant',
      color: 'text-emerald-400',
      description: 'Your health data is protected'
    },
    {
      icon: CheckCircle,
      text: 'CLIA Certified',
      color: 'text-blue-400',
      description: 'Laboratory quality standards'
    },
    {
      icon: Award,
      text: 'HSA/FSA Eligible',
      color: 'text-cyan-400',
      description: 'Tax-advantaged spending'
    }
  ]

  const socialLinks = [
    { name: 'Twitter', href: 'https://twitter.com/biotune', icon: Twitter },
    { name: 'Facebook', href: 'https://facebook.com/biotune', icon: Facebook },
    { name: 'Instagram', href: 'https://instagram.com/biotune', icon: Instagram },
    { name: 'LinkedIn', href: 'https://linkedin.com/company/biotune', icon: Linkedin }
  ]

  const contactInfo = [
    { icon: Mail, text: 'dhruvaparik@gmail.com', href: 'mailto:dhruvaparik@gmail.com' },
    { icon: Phone, text: '+1 639 318 1891', href: 'tel:+16393181891' },
    { icon: MapPin, text: 'Hamilton, Ontario, Canada', href: '#' }
  ]

  return (
    <footer className="bg-theme-bg text-theme-text relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_20%_80%,rgba(6,182,212,0.1),transparent_50%),radial-gradient(circle_at_80%_20%,rgba(16,185,129,0.1),transparent_50%)]" />
      </div>

      <div className="relative z-10">
        {/* Trust Badges Section */}
        <div className="border-b border-theme-border">
          <Container>
            <div className="py-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {trustBadges.map((badge, index) => (
                  <motion.div
                    key={badge.text}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center space-x-3 p-4 rounded-lg bg-theme-surface border border-theme-border shadow-sm"
                  >
                    <badge.icon className={`w-6 h-6 ${badge.color} flex-shrink-0`} />
                    <div>
                      <p className="font-semibold text-theme-text">{badge.text}</p>
                      <p className="text-sm text-theme-secondary">{badge.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </Container>
        </div>

        {/* Main Footer Content */}
        <div className="border-b border-theme-border">
          <Container>
            <div className="py-16 grid grid-cols-1 lg:grid-cols-12 gap-8">
              {/* Brand Section */}
              <div className="lg:col-span-4">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="space-y-6"
                >
                  {/* Logo */}
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-theme-primary to-theme-secondary rounded-xl flex items-center justify-center shadow-lg">
                      <Heart className="w-6 h-6 text-theme-text-inverse" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-theme-text">BioTune</h3>
                      <p className="text-sm text-theme-secondary">Monthly Labs + AI Health OS</p>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-theme-secondary leading-relaxed max-w-sm">
                    Advanced biomarker testing combined with AI-powered insights to help you optimize your health and extend your healthy lifespan.
                  </p>

                  {/* Newsletter Signup */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-theme-text flex items-center">
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Stay Updated
                    </h4>
                    <div className="flex gap-2">
                      <input
                        type="email"
                        placeholder="Enter your email"
                        className="flex-1 px-4 py-2 bg-theme-surface border border-theme-border rounded-lg text-theme-text-primary placeholder-theme-text-muted focus:outline-none focus:ring-2 focus:ring-theme-primary focus:border-transparent"
                      />
                      <button
                        className="px-4 py-2 bg-theme-primary hover:bg-theme-secondary rounded-lg text-theme-text-inverse font-medium transition-colors flex items-center"
                        onClick={() => handleComingSoon('Newsletter Subscription')}
                      >
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Navigation Sections */}
              <div className="lg:col-span-8 grid grid-cols-2 md:grid-cols-4 gap-8">
                {Object.entries(footerSections).map(([section, links], index) => (
                  <motion.div
                    key={section}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2 + index * 0.1 }}
                    className="space-y-4"
                  >
                    <h4 className="font-semibold text-theme-text capitalize">
                      {section.replace(/([A-Z])/g, ' $1').trim()}
                    </h4>
                    <ul className="space-y-2">
                      {links.map((link) => (
                        <li key={link.name}>
                          <Link
                            to={link.href}
                            className="text-theme-secondary hover:text-theme-primary transition-colors text-sm flex items-center group"
                          >
                            {link.icon && (
                              <link.icon className="w-3 h-3 mr-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                            )}
                            <span className="group-hover:translate-x-1 transition-transform">
                              {link.name}
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                ))}
              </div>
            </div>
          </Container>
        </div>

        {/* Contact & Social Section */}
        <div className="border-b border-theme-border">
          <Container>
            <div className="py-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Contact Information */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-theme-text">Get in Touch</h4>
                  <div className="space-y-3">
                    {contactInfo.map((info) => (
                      <a
                        key={info.text}
                        href={info.href}
                        className="flex items-center space-x-3 text-theme-secondary hover:text-theme-primary transition-colors group"
                      >
                        <info.icon className="w-4 h-4 text-theme-primary group-hover:text-theme-secondary" />
                        <span>{info.text}</span>
                      </a>
                    ))}
                  </div>
                </div>

                {/* Social Links */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-theme-text">Follow Us</h4>
                  <div className="flex space-x-4">
                    {socialLinks.map((social) => (
                      <a
                        key={social.name}
                        href={social.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-10 h-10 bg-theme-primary/10 hover:bg-theme-primary rounded-lg flex items-center justify-center text-theme-primary hover:text-theme-text-inverse transition-all duration-200 group border border-theme-primary/30"
                        aria-label={`Follow us on ${social.name}`}
                      >
                        <social.icon className="w-4 h-4 group-hover:scale-110 transition-transform" />
                      </a>
                    ))}
                  </div>
                  <p className="text-sm text-theme-secondary">
                    Join our community for health tips and updates
                  </p>
                </div>
              </div>
            </div>
          </Container>
        </div>

        {/* Bottom Section */}
        <div className="py-6">
          <Container>
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <div className="text-theme-secondary text-sm text-center md:text-left">
                <p>© {currentYear} BioTune Health Optimization. All rights reserved.</p>
                <p className="mt-1 text-xs">
                  Medical disclaimer: This service provides health insights and is not intended to diagnose, treat, cure, or prevent any disease.
                </p>
              </div>

              <div className="flex items-center space-x-6 text-sm">
                <Link
                  to="/privacy"
                  className="text-theme-secondary hover:text-theme-primary transition-colors"
                >
                  Privacy Policy
                </Link>
                <Link
                  to="/terms"
                  className="text-theme-secondary hover:text-theme-primary transition-colors"
                >
                  Terms of Service
                </Link>
                <Link
                  to="/accessibility"
                  className="text-theme-secondary hover:text-theme-primary transition-colors"
                >
                  Accessibility
                </Link>
              </div>
            </div>
          </Container>
        </div>
      </div>
    </footer>
  )
}

export default Footer